import React from 'react';
import { Link } from 'react-router-dom';

const gradientBg = "bg-gradient-to-br from-[#0f2027] via-[#2c5364] to-[#1a2980]";
const glassCard = "bg-white/5 backdrop-blur-lg rounded-2xl shadow-xl border border-white/10 p-6";

const PrivacyPolicy: React.FC = () => {
  return (
    <div className={`min-h-screen w-full ${gradientBg} py-8 px-4`}>
      <div className="max-w-4xl mx-auto">
        <div className={glassCard}>
          {/* Header */}
          <div className="text-center mb-8">
            <h1 className="text-4xl font-extrabold text-emerald-100 mb-4">
              Politique de Confidentialité
            </h1>
            <p className="text-emerald-200">
              Dernière mise à jour : 21 octobre 2025
            </p>
          </div>

          <div className="space-y-8 text-emerald-200">
            {/* Introduction */}
            <section>
              <h2 className="text-2xl font-bold text-cyan-300 mb-4">1. Introduction</h2>
              <p className="leading-relaxed mb-4">
                Chez Jupipotes, nous prenons la protection de vos données personnelles très au sérieux. 
                Cette politique de confidentialité explique comment nous collectons, utilisons, partageons 
                et protégeons vos informations lorsque vous utilisez notre plateforme.
              </p>
              <p className="leading-relaxed">
                En utilisant Jupipotes, vous acceptez les pratiques décrites dans cette politique. 
                Si vous n'êtes pas d'accord avec ces pratiques, veuillez ne pas utiliser notre service.
              </p>
            </section>

            {/* Informations collectées */}
            <section>
              <h2 className="text-2xl font-bold text-cyan-300 mb-4">2. Informations que nous collectons</h2>
              
              <h3 className="text-xl font-semibold text-emerald-100 mb-3">2.1 Informations que vous fournissez</h3>
              <ul className="list-disc list-inside space-y-2 mb-4 ml-4">
                <li>Informations de profil (nom d'utilisateur, photo, bio)</li>
                <li>Adresse email</li>
                <li>Adresse de wallet Web3 (si applicable)</li>
                <li>Contenu que vous publiez (posts, commentaires, messages)</li>
                <li>Préférences et paramètres</li>
              </ul>

              <h3 className="text-xl font-semibold text-emerald-100 mb-3">2.2 Informations collectées automatiquement</h3>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Adresse IP et données de connexion</li>
                <li>Type de navigateur et système d'exploitation</li>
                <li>Pages visitées et temps passé sur la plateforme</li>
                <li>Interactions avec le contenu</li>
                <li>Cookies et technologies similaires</li>
              </ul>
            </section>

            {/* Utilisation des données */}
            <section>
              <h2 className="text-2xl font-bold text-cyan-300 mb-4">3. Comment nous utilisons vos informations</h2>
              <p className="mb-3">Nous utilisons vos informations pour :</p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Fournir et améliorer nos services</li>
                <li>Personnaliser votre expérience utilisateur</li>
                <li>Gérer votre compte et vos préférences</li>
                <li>Communiquer avec vous (notifications, mises à jour)</li>
                <li>Assurer la sécurité et prévenir la fraude</li>
                <li>Analyser l'utilisation de la plateforme</li>
                <li>Respecter nos obligations légales</li>
                <li>Distribuer les récompenses en tokens JUP</li>
              </ul>
            </section>

            {/* Partage des données */}
            <section>
              <h2 className="text-2xl font-bold text-cyan-300 mb-4">4. Partage de vos informations</h2>
              <p className="mb-3">Nous ne vendons jamais vos données personnelles. Nous pouvons partager vos informations avec :</p>
              
              <h3 className="text-xl font-semibold text-emerald-100 mb-3">4.1 Autres utilisateurs</h3>
              <p className="mb-4">
                Les informations de votre profil public et le contenu que vous publiez sont visibles 
                par les autres utilisateurs de Jupipotes.
              </p>

              <h3 className="text-xl font-semibold text-emerald-100 mb-3">4.2 Fournisseurs de services</h3>
              <p className="mb-4">
                Nous travaillons avec des fournisseurs de services tiers (hébergement, analytics, 
                paiements) qui traitent des données en notre nom et sous nos instructions.
              </p>

              <h3 className="text-xl font-semibold text-emerald-100 mb-3">4.3 Conformité légale</h3>
              <p>
                Nous pouvons divulguer vos informations si la loi l'exige ou pour protéger nos droits, 
                notre propriété ou la sécurité de nos utilisateurs.
              </p>
            </section>

            {/* Sécurité */}
            <section>
              <h2 className="text-2xl font-bold text-cyan-300 mb-4">5. Sécurité des données</h2>
              <p className="mb-3">
                Nous mettons en œuvre des mesures de sécurité techniques et organisationnelles 
                appropriées pour protéger vos données, notamment :
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Chiffrement des données en transit et au repos</li>
                <li>Authentification à deux facteurs (2FA)</li>
                <li>Contrôles d'accès stricts</li>
                <li>Surveillance et audits réguliers</li>
                <li>Sauvegardes sécurisées</li>
              </ul>
            </section>

            {/* Blockchain et Web3 */}
            <section>
              <h2 className="text-2xl font-bold text-cyan-300 mb-4">6. Données Blockchain et Web3</h2>
              <p className="leading-relaxed mb-4">
                Si vous connectez un wallet Web3, certaines de vos activités (transactions de tokens, 
                votes DAO) seront enregistrées sur la blockchain publique et seront permanentes et 
                visibles publiquement.
              </p>
              <p className="leading-relaxed bg-cyan-900/20 p-4 rounded-lg border border-cyan-700/30">
                ⚠️ <strong>Important :</strong> Les transactions blockchain sont immuables et ne peuvent 
                pas être supprimées ou modifiées une fois enregistrées.
              </p>
            </section>

            {/* Vos droits */}
            <section>
              <h2 className="text-2xl font-bold text-cyan-300 mb-4">7. Vos droits</h2>
              <p className="mb-3">Vous avez le droit de :</p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li><strong>Accéder</strong> à vos données personnelles</li>
                <li><strong>Rectifier</strong> les informations inexactes</li>
                <li><strong>Supprimer</strong> votre compte et vos données</li>
                <li><strong>Exporter</strong> vos données dans un format portable</li>
                <li><strong>Limiter</strong> le traitement de vos données</li>
                <li><strong>Vous opposer</strong> à certains traitements</li>
                <li><strong>Retirer</strong> votre consentement à tout moment</li>
              </ul>
              <p className="mt-4 bg-emerald-900/20 p-4 rounded-lg border border-emerald-700/30">
                Pour exercer ces droits, contactez-nous via la <Link to="/support" className="text-cyan-300 hover:text-cyan-200 underline">page Support</Link>.
              </p>
            </section>

            {/* Cookies */}
            <section>
              <h2 className="text-2xl font-bold text-cyan-300 mb-4">8. Cookies et technologies similaires</h2>
              <p className="mb-3">
                Nous utilisons des cookies et technologies similaires pour améliorer votre expérience. 
                Types de cookies utilisés :
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li><strong>Essentiels :</strong> Nécessaires au fonctionnement de la plateforme</li>
                <li><strong>Fonctionnels :</strong> Mémorisent vos préférences</li>
                <li><strong>Analytiques :</strong> Nous aident à comprendre l'utilisation</li>
                <li><strong>Performance :</strong> Améliorent la vitesse et fiabilité</li>
              </ul>
              <p className="mt-4">
                Vous pouvez gérer vos préférences de cookies dans les paramètres de votre navigateur.
              </p>
            </section>

            {/* Conservation des données */}
            <section>
              <h2 className="text-2xl font-bold text-cyan-300 mb-4">9. Conservation des données</h2>
              <p className="leading-relaxed">
                Nous conservons vos données personnelles aussi longtemps que nécessaire pour fournir 
                nos services et respecter nos obligations légales. Lorsque vous supprimez votre compte, 
                nous supprimons ou anonymisons vos données dans un délai raisonnable, sauf obligation 
                légale de conservation.
              </p>
            </section>

            {/* Transferts internationaux */}
            <section>
              <h2 className="text-2xl font-bold text-cyan-300 mb-4">10. Transferts internationaux</h2>
              <p className="leading-relaxed">
                Vos données peuvent être transférées et traitées dans des pays autres que votre pays 
                de résidence. Nous veillons à ce que ces transferts soient effectués conformément aux 
                lois applicables en matière de protection des données.
              </p>
            </section>

            {/* Mineurs */}
            <section>
              <h2 className="text-2xl font-bold text-cyan-300 mb-4">11. Protection des mineurs</h2>
              <p className="leading-relaxed">
                Jupipotes est destiné aux utilisateurs de 13 ans et plus. Nous ne collectons pas 
                sciemment d'informations personnelles auprès d'enfants de moins de 13 ans. Si vous 
                pensez qu'un enfant nous a fourni des informations, contactez-nous immédiatement.
              </p>
            </section>

            {/* Modifications */}
            <section>
              <h2 className="text-2xl font-bold text-cyan-300 mb-4">12. Modifications de cette politique</h2>
              <p className="leading-relaxed">
                Nous pouvons mettre à jour cette politique de confidentialité périodiquement. Nous vous 
                informerons de tout changement important par email ou via une notification sur la plateforme. 
                La date de "dernière mise à jour" en haut de cette page indique quand la politique a été 
                révisée pour la dernière fois.
              </p>
            </section>

            {/* Contact */}
            <section>
              <h2 className="text-2xl font-bold text-cyan-300 mb-4">13. Nous contacter</h2>
              <p className="leading-relaxed mb-4">
                Pour toute question concernant cette politique de confidentialité ou vos données 
                personnelles, contactez-nous :
              </p>
              <div className="bg-cyan-900/20 p-4 rounded-lg border border-cyan-700/30">
                <p className="mb-2"><strong>Email :</strong> privacy@jupipotes.fr</p>
                <p><strong>Support :</strong> <Link to="/support" className="text-cyan-300 hover:text-cyan-200 underline">Page de contact</Link></p>
              </div>
            </section>
          </div>

          {/* Navigation */}
          <div className="mt-12 pt-6 border-t border-white/10 flex flex-col sm:flex-row justify-between items-center gap-4">
            <Link
              to="/terms"
              className="text-cyan-300 hover:text-cyan-200 transition-colors flex items-center gap-2"
            >
              Conditions d'utilisation
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </Link>
            <Link
              to="/JUPResources"
              className="text-emerald-300 hover:text-emerald-200 transition-colors flex items-center gap-2"
            >
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              Retour aux ressources
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
